import { formatEntryType } from 'helpers/eventUtils';
import styles from 'pages/Calendar/EventInfo/tabs/EphanCategories/EphanCategories.scss';

/**
 * Categories of a competition imported from e-phan.
 *
 * e-phan publishes a flat list rather than the timed departments an ECDS event
 * has, so they are grouped by age group here — that is the structure its own
 * calendar shows and the one dancers look for.
 */

const STYLE_LABELS = { latin: 'Latin', standard: 'Standard', combined: 'Combined', none: '—' };

const groupByAgeGroup = (categories) => {
  const groups = new Map();

  [...categories]
    .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name))
    .forEach((category) => {
      const key = category.ageGroup || 'Other';

      if (!groups.has(key)) {
        groups.set(key, { ageGroup: key, birthYearRange: category.birthYearRange, categories: [] });
      }

      groups.get(key).categories.push(category);
    });

  return [...groups.values()];
};

/**
 * Entries that picked this category. Deliberately summed from the categories
 * rather than taken from the competition's entriesCount: one entry may sign up
 * for several categories, so the two count different things and adding them
 * together would overstate the group.
 */
const startsIn = (categories) =>
  categories.reduce((total, { startsCount }) => total + (Number.isFinite(startsCount) ? startsCount : 0), 0);

export const EphanCategories = ({ data: { ephanCategories, registration } }) => {
  // Counts only mean something while entries are being taken; before that they
  // are a column of zeroes.
  const showStarts = registration?.accept === true;

  return groupByAgeGroup(ephanCategories ?? []).map(({ ageGroup, birthYearRange, categories }) => (
    <div className={styles.group} key={ageGroup}>
      <div className={styles.groupHeader}>
        <span className={styles.groupName}>{ageGroup}</span>
        {birthYearRange && <span className={styles.birthYears}>{birthYearRange}</span>}
      </div>
      {categories.map(({ ephanCategoryId, name, entryType, style, level, dances, startsCount }) => (
        <div className={styles.category} key={ephanCategoryId}>
          <span className={styles.categoryName}>{name}</span>
          <span className={styles.entryType}>{formatEntryType(entryType)}</span>
          <span className={styles.style}>{STYLE_LABELS[style] ?? style ?? '—'}</span>
          <div className={styles.stacked}>
            <span>{name}</span>
            <span>
              {[formatEntryType(entryType), STYLE_LABELS[style] ?? style].filter(Boolean).join(' · ')}
            </span>
          </div>
          <span className={styles.dances}>{(dances ?? []).join(', ')}</span>
          <span className={styles.level}>{level ?? '—'}</span>
          {showStarts && <span className={styles.starts}>{startsCount ?? 0}</span>}
        </div>
      ))}
      {showStarts && (
        <div className={styles.groupTotals}>
          <span>Entries</span>
          <span className={styles.starts}>{startsIn(categories)}</span>
        </div>
      )}
    </div>
  ));
};
